'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { BREAKPOINTS, STORAGE_KEYS } from '@/constants/layout';

interface NavContextType {
  isExpanded: boolean;
  toggleNav: () => void;
  setIsExpanded: (value: boolean) => void;
}

const NavContext = createContext<NavContextType | undefined>(undefined);

export const useNav = () => {
  const context = useContext(NavContext);
  if (!context) {
    throw new Error('useNav must be used within NavProvider');
  }
  return context;
};

interface NavProviderProps {
  children: ReactNode;
}

export const NavProvider: React.FC<NavProviderProps> = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Debounced resize handler to prevent excessive state updates
  const handleResponsiveState = useCallback(() => {
    const isTablet = window.innerWidth <= BREAKPOINTS.TABLET;

    if (isTablet) {
      setIsExpanded(false);
    } else {
      const savedState = localStorage.getItem(STORAGE_KEYS.SIDENAV_EXPANDED);
      setIsExpanded(savedState !== 'false');
    }
  }, []);

  useEffect(() => {
    setMounted(true);
    handleResponsiveState();

    // Debounce resize events to reduce performance impact
    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResponsiveState, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [handleResponsiveState]);

  useEffect(() => {
    if (mounted && window.innerWidth > BREAKPOINTS.TABLET) {
      localStorage.setItem(STORAGE_KEYS.SIDENAV_EXPANDED, String(isExpanded));
    }
  }, [isExpanded, mounted]);

  // Toggle nav-collapsed class on body element
  useEffect(() => {
    if (!mounted) return;

    if (isExpanded) {
      document.body.classList.remove('nav-collapsed');
    } else {
      document.body.classList.add('nav-collapsed');
    }
  }, [isExpanded, mounted]);

  const toggleNav = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <NavContext.Provider value={{ isExpanded, toggleNav, setIsExpanded }}>
      {children}
    </NavContext.Provider>
  );
};
